/**
 * Pulso del sistema: cuántas peticiones están en vuelo y cómo terminó la última
 * escritura. Lo alimenta `api/client.ts` y lo lee la barra de actividad del
 * shell — la animación de la barra no es decoración: se mueve PORQUE el sistema
 * está trabajando, y su desenlace (verde/rojo) es el de la acción que hiciste.
 *
 * Store mínimo (sin dependencias) leído con useSyncExternalStore.
 */

export type ActivityOutcome = 'success' | 'error'

export interface ActivitySnapshot {
  /** Peticiones de lectura en vuelo. */
  reads: number
  /** Escrituras (POST/PUT/PATCH/DELETE) en vuelo: pesan más visualmente. */
  writes: number
  /** Desenlace de la última escritura; `seq` cambia en cada una para re-disparar animaciones. */
  last: { outcome: ActivityOutcome; seq: number } | null
}

let state: ActivitySnapshot = { reads: 0, writes: 0, last: null }
const listeners = new Set<() => void>()
let seq = 0

function emit(next: ActivitySnapshot) {
  state = next
  listeners.forEach(l => l())
}

const isWrite = (method: string) => method !== 'GET' && method !== 'HEAD'

export function trackRequest<T>(method: string, run: () => Promise<T>): Promise<T> {
  const write = isWrite(method.toUpperCase())
  emit(write ? { ...state, writes: state.writes + 1 } : { ...state, reads: state.reads + 1 })

  const settle = (outcome: ActivityOutcome) => {
    const base = write ? { ...state, writes: Math.max(0, state.writes - 1) } : { ...state, reads: Math.max(0, state.reads - 1) }
    // Solo las escrituras anuncian desenlace: una lectura fallida ya la reporta el toast.
    emit(write ? { ...base, last: { outcome, seq: ++seq } } : base)
  }

  return run().then(
    v => { settle('success'); return v },
    e => { settle('error'); throw e },
  )
}

export const activityStore = {
  subscribe(listener: () => void) {
    listeners.add(listener)
    return () => { listeners.delete(listener) }
  },
  getSnapshot: () => state,
}
